import { useMemo, useCallback, useState, useRef, Dispatch, SetStateAction, RefObject } from 'react';
import { max, min } from 'd3';
import type { CategoricalChartState } from 'recharts/types/chart/types';
import { metersToFeet, metersToKilometers, metersToMiles } from '@alltrails/data-formatting';
import { averageGrade } from '../utils/legacyGeoJSONConversions';
import { ElevationData, ElevationPoint, LngLat } from '../types/Geo';
import getElevationDataMinMaxChartValues from '../utils/getElevationDataMinMaxChartValues';

type Args = {
  displayMetric?: boolean;
  elevationData: ElevationPoint[][];
  handleMouseOver: (coordinates: LngLat | null) => void;
  setCurrentGrade: Dispatch<SetStateAction<string | number>>;
  shouldRenderMinimalTicks?: boolean;
  tooltipRef: RefObject<HTMLDivElement>;
  activeSegmentIndex?: number;
};

const useElevationChart = ({
  displayMetric,
  elevationData,
  handleMouseOver,
  setCurrentGrade,
  shouldRenderMinimalTicks,
  tooltipRef,
  activeSegmentIndex
}: Args): {
  data: ElevationData;
  handleMouseMove: (chart: CategoricalChartState) => void;
  showEmptyData: boolean;
  tooltipXPosition: number;
  xTicks: number[];
  yTicks: number[];
} => {
  const gradeCache = useRef<Record<string, number>>({});
  const [tooltipXPosition, setTooltipXPosition] = useState<number>(0);

  const { minActivePointIndex, maxActivePointIndex } = useMemo(() => {
    let min = 0;
    let max = elevationData.length - 1;

    if (activeSegmentIndex !== undefined && elevationData[activeSegmentIndex]) {
      min = elevationData.slice(0, activeSegmentIndex).reduce((acc, segment) => acc + segment.length, 0);
      max = min + elevationData[activeSegmentIndex].length - 2;
    }

    return {
      minActivePointIndex: min,
      maxActivePointIndex: max
    };
  }, [elevationData, activeSegmentIndex]);

  const flattenedData = useMemo(
    () =>
      elevationData
        .flat()
        .filter(segment => Boolean(segment?.length))
        .map(point => {
          const distance = displayMetric ? metersToKilometers(point[0]) : metersToMiles(point[0]);
          const elevation = displayMetric ? point[1] : metersToFeet(point[1]);
          return [distance, elevation, ...point.slice(2)] as ElevationPoint;
        }),
    [displayMetric, elevationData]
  );

  const showEmptyData = useMemo(() => flattenedData.length === 0, [flattenedData]);

  const data = useMemo<ElevationData>(
    () =>
      flattenedData.length < 1
        ? [{ distance: 0, elevation: 0, coordinates: [0, 0], index: 0 }]
        : flattenedData.map((dataPoint, index) => {
            const distance = dataPoint?.[0];
            const elevation = dataPoint?.[1];
            const coordinates = dataPoint?.[2];
            const currentTime = dataPoint?.[3]?.currentTime;
            const elapsedTime = dataPoint?.[3]?.elapsedTime;

            return {
              distance,
              elevation,
              // If this point is outside of the active segment return the elevation as inactiveElevation
              inactiveElevation: index < minActivePointIndex || index > maxActivePointIndex ? elevation : undefined,
              coordinates,
              index,
              currentTime,
              elapsedTime
            };
          }),
    [flattenedData, minActivePointIndex, maxActivePointIndex]
  );

  const xTicks = useMemo(() => {
    const maxX = max(flattenedData, d => d?.[0]) || 0;
    if (maxX === 0 || showEmptyData) {
      return [0, 100];
    }
    const xTickMultipliers = shouldRenderMinimalTicks ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1];
    return xTickMultipliers.map(mult => maxX * mult);
  }, [flattenedData, shouldRenderMinimalTicks, showEmptyData]);

  const yTicks = useMemo(() => {
    const minY = min(flattenedData, d => d?.[1]) || 0;
    const maxY = max(flattenedData, d => d?.[1]) || 0;
    return getElevationDataMinMaxChartValues(minY, maxY, displayMetric);
  }, [flattenedData, displayMetric]);

  const handleMouseMove = useCallback(
    (chart: CategoricalChartState) => {
      const coordinates = chart?.activePayload?.[0]?.payload?.coordinates;
      const cursorX = chart?.chartX || 0;
      const offset = 16;
      const tooltipWidth = tooltipRef.current?.clientWidth ?? 100;
      // position the tooltip to the left of the cursor when there is enough space to do so:
      const tooltipX = cursorX > tooltipWidth + offset ? cursorX - tooltipWidth - offset : cursorX + offset;
      setTooltipXPosition(tooltipX);

      if (!coordinates) {
        setCurrentGrade('');
        handleMouseOver?.(null);
      } else {
        const index = chart?.activeTooltipIndex;
        const cacheKey = JSON.stringify(index);
        if (index && flattenedData.length > 3 && index > -1 && index < flattenedData.length - 1) {
          const grade = gradeCache?.current?.[cacheKey] || averageGrade(flattenedData, index, displayMetric);
          gradeCache.current[cacheKey] = grade;
          setCurrentGrade(grade ?? null);
        } else {
          setCurrentGrade('');
        }

        handleMouseOver?.([coordinates?.[1], coordinates?.[0]]);
      }
    },
    [tooltipRef, setCurrentGrade, handleMouseOver, flattenedData, displayMetric]
  );

  return { data, handleMouseMove, showEmptyData, tooltipXPosition, xTicks, yTicks };
};

export default useElevationChart;
